/** @jsxImportSource @emotion/react */
import React from "react";
import { useState, useEffect } from "react";
import styled from "@emotion/styled";

import SettingsIcon from "@material-ui/icons/Settings";
import Popover from "@material-ui/core/Popover";
import Typography from "@material-ui/core/Typography";

import ToolbarButton from "./ToolbarButton";
import Switch from "@material-ui/core/Switch";
import t from "translations";

import { useStoreon } from "storeon/react";
import { fabric } from "fabric";

const OptionsButton = (props) => {
  const { canvas, needRender } = props;
  const { dispatch, snapValue } = useStoreon("snapValue");

  //popover
  const [anchorEl, setAnchorEl] = useState(null);
  const isPopoverOpened = Boolean(anchorEl);
  const popoverID = isPopoverOpened ? "settings-popover" : undefined;
  const [snapState, setSnapState] = useState(snapValue);
  const g_delta = 5;
  const g_DirectionsColor = "#a51b1b";

  var addedDirections = [];
  var commonLines = [];

  const handleObjectMoved = (e) => {
    removeLines();
  };

  const handleObjectMoving = (e) => {
    if (snapState === false) return;

    let isMoreThanOneSelected = e.target["_objects"] ? 1 : 0;
    removeLines();

    if (isMoreThanOneSelected) return;

    canvas.bringToFront(e.target); // when draggin i-text over image

    let directionsArray = [];
    let linesArr = getDirectionLines(e.target);

    canvas.getObjects().forEach((obj) => {
      if (obj === e.target) return;

      if (obj.type === "image" || obj.type === "i-text") {
        let dirArr = getDirectionLines(obj);
        directionsArray = directionsArray.concat(dirArr);
      }
    });
    // compare with common direcion lines
    directionsArray = directionsArray.concat(commonLines);

    // for objects on canvas
    for (let line of directionsArray)
      compareDirectionLines(linesArr, line, e.target);
  };

  useEffect(() => {
    if (!canvas) return;

    // defalut values for center directions
    commonLines.push({
      x1: canvas.backgroundImage.width / 2,
      x2: canvas.backgroundImage.width / 2,
      y1: 0,
      y2: canvas.backgroundImage.height,
    });
    commonLines.push({
      x1: 0,
      x2: canvas.backgroundImage.width,
      y1: canvas.backgroundImage.height / 2,
      y2: canvas.backgroundImage.height / 2,
    });

    canvas.on("object:moving", handleObjectMoving);
    canvas.on("object:moved", handleObjectMoved);

    return () => {
      canvas.off("object:moving", handleObjectMoving);
      canvas.off("object:moved", handleObjectMoved);
    };
  }, [canvas]);

  if (!canvas || !needRender) return null;

  function removeLines() {
    for (let l of addedDirections) canvas.remove(l);

    addedDirections = [];
  }

  function getDirectionLines(obj) {
    let x1 = obj.left,
      x2 = obj.left + obj.width * obj.scaleX,
      y1 = obj.top,
      y2 = obj.top + obj.height * obj.scaleY,
      cx = obj.left + (obj.width * obj.scaleX) / 2,
      cy = obj.top + (obj.height * obj.scaleY) / 2;

    let l1 = { x1: x1, x2: x1, y1: 0, y2: canvas.backgroundImage.height },
      l2 = { x1: x2, x2: x2, y1: 0, y2: canvas.backgroundImage.height },
      l3 = { x1: 0, x2: canvas.backgroundImage.width, y1: y1, y2: y1 },
      l4 = { x1: 0, x2: canvas.backgroundImage.width, y1: y2, y2: y2 },
      l5 = { x1: cx, x2: cx, y1: 0, y2: canvas.backgroundImage.height },
      l6 = { x1: 0, x2: canvas.backgroundImage.width, y1: cy, y2: cy };

    return [l1, l2, l3, l4, l5, l6];
  }

  function snap2Direction(line, direction, activeTarget) {
    switch (direction) {
      case 0:
        activeTarget.left = line.x1;
        break;
      case 1:
        activeTarget.left = line.x1 - activeTarget.width * activeTarget.scaleX;
        break;
      case 2:
        activeTarget.top = line.y1;
        break;
      case 3:
        activeTarget.top = line.y1 - activeTarget.height * activeTarget.scaleY;
        break;
      case 4:
        activeTarget.left =
          line.x1 - (activeTarget.width * activeTarget.scaleX) / 2;
        break;
      case 5:
        activeTarget.top =
          line.y1 - (activeTarget.height * activeTarget.scaleY) / 2;
        break;

      default:
        break;
    }

    activeTarget.setCoords();
  }

  function drawDirection(line) {
    const { cutW, cutH } = canvas;
    const stroke = cutW > cutH ? cutW / 1000 : cutH / 1000;
    const strokeWidth = stroke > 1.5 ? 1.5 : stroke;
    let l = new fabric.Line([line.x1, line.y1, line.x2, line.y2], {
      strokeDashArray: [strokeWidth*5, strokeWidth*5],
      stroke: g_DirectionsColor,
      strokeWidth: strokeWidth,
      selectable: false,
      evented: false,
    });
    canvas.add(l);
    addedDirections.push(l);
  }

  function compareDirectionLines(lArr, line, activeTarget) {
    for (let i = 0; i < lArr.length; i++) {
      let l = lArr[i];
      if (
        Math.abs(l.x1 - line.x1) < g_delta &&
        Math.abs(l.x2 - line.x2) < g_delta &&
        Math.abs(l.y1 - line.y1) < g_delta &&
        Math.abs(l.y2 - line.y2) < g_delta
      ) {
        drawDirection(line);
        snap2Direction(line, i, activeTarget);
      }
    }
  }

  const handleStateChange = (event) => {
    dispatch("settings/snapvalue", !snapState);

    if (event.target) setSnapState(event.target.checked);
    else setSnapState(event);
  };
  /////////////////////////////////////////////
  const PopupDlg = styled.div`
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  `;

  return (
    <React.Fragment>
      <ToolbarButton
        desc={t.settings_btn}
        tooltip
        icon={<SettingsIcon />}
        aria-describedby={popoverID}
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
      />
      <Popover
        id={popoverID}
        open={isPopoverOpened}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <PopupDlg>
          <Switch checked={snapState} onChange={handleStateChange} />
          <Typography
            onClick={(event) => {
              handleStateChange(snapState ? false : true);
            }}
          >
            {t.settings_btn_autosnap}
          </Typography>
        </PopupDlg>
      </Popover>
    </React.Fragment>
  );
};

export default OptionsButton;
